import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  alpha,
  Badge,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  keyframes,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Check, Close, Forum } from '@mui/icons-material';
import type { GreeningQuestionItem } from '../../services/greeningApi';
import type { UseGreeningAnalysisResult } from '../../hooks/useGreeningAnalysis';
import {
  GREENING_BRAND,
  GREENING_FAB_ACTIONS,
  GREENING_FAB_MENU_SUBTITLE,
} from './greeningFabActions';
import { GREENING_AGENT_LABEL } from './constants';
import { getGreeningChatAvailability } from './greeningChatAvailability';
import { GreeningBrandLabel, GreeningChatTitle } from './GreeningBrandLabel';
import { GreeningChatDialog } from './GreeningChatDialog';
import { GreeningChatUnavailableDialog } from './GreeningChatUnavailableDialog';
import { GreeningIcon } from './GreeningIcon';
import { greeningQuestionCount } from './greeningQuestionCount';

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 ${alpha(GREENING_BRAND.green, 0.45)}; }
  70% { box-shadow: 0 0 0 8px ${alpha(GREENING_BRAND.green, 0)}; }
  100% { box-shadow: 0 0 0 0 ${alpha(GREENING_BRAND.green, 0)}; }
`;

interface Props {
  greening: UseGreeningAnalysisResult;
}

export function GreeningChatFab({ greening }: Props) {
  const theme = useTheme();
  const rootRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [unavailableOpen, setUnavailableOpen] = useState(false);

  const availability = useMemo(
    () => getGreeningChatAvailability(greening.analysis, greening.analysisLoading),
    [greening.analysis, greening.analysisLoading],
  );

  const items: GreeningQuestionItem[] = greening.history;
  const messageCount = greeningQuestionCount(items);
  const hasPending = items.some(
    (item) => item.status === 'pending' || item.status === 'processing' || item.status === 'retrying',
  );

  const isStudying =
    availability.kind === 'primary_active' || availability.kind === 'five_whys_active';
  const isError = availability.kind === 'primary_error' || availability.kind === 'five_whys_error';
  const isReady = availability.canAsk;

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (rootRef.current && target && !rootRef.current.contains(target)) {
        closeMenu();
      }
    };
    document.addEventListener('click', onPointerDown, true);
    return () => {
      document.removeEventListener('click', onPointerDown, true);
    };
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };
    if (menuOpen) document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen, closeMenu]);

  const handleOpenChat = () => {
    closeMenu();
    if (availability.kind === 'loading') return;
    if (availability.canAsk) {
      setChatOpen(true);
      return;
    }
    if (messageCount > 0) {
      setChatOpen(true);
      return;
    }
    setUnavailableOpen(true);
  };

  const fabBadgeContent = useMemo(() => {
    if (hasPending) return '•';
    if (messageCount > 0) return messageCount;
    if (isReady) return <Check sx={{ fontSize: 12 }} />;
    if (isError) return '!';
    return undefined;
  }, [hasPending, messageCount, isReady, isError]);

  const fabBadgeColor: 'success' | 'warning' | 'error' | 'default' = hasPending || isReady
    ? 'success'
    : isError
      ? 'error'
      : 'default';

  const pendingBadgeSx: SxProps<Theme> = {
    '& .MuiBadge-badge': {
      minWidth: 10,
      height: 10,
      borderRadius: '50%',
      p: 0,
      top: 6,
      right: 6,
    },
  };

  const countBadgeSx: SxProps<Theme> = {
    '& .MuiBadge-badge': {
      top: 4,
      right: 4,
      minWidth: 18,
      height: 18,
      fontSize: '0.65rem',
      fontWeight: 700,
    },
  };

  const readyBadgeSx: SxProps<Theme> = {
    '& .MuiBadge-badge': {
      top: 4,
      right: 4,
      minWidth: 18,
      height: 18,
      p: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  const fabOutlineColor = isReady
    ? GREENING_BRAND.green
    : isError
      ? theme.palette.error.main
      : GREENING_BRAND.green;

  const fabBadgeSx = hasPending
    ? pendingBadgeSx
    : messageCount > 0
      ? countBadgeSx
      : isReady
        ? readyBadgeSx
        : pendingBadgeSx;

  const chatAction = GREENING_FAB_ACTIONS[0];
  const chatMenuDescription = availability.canAsk
    ? chatAction.description
    : isStudying
      ? `O agente ${GREENING_AGENT_LABEL} ainda está estudando — retorne mais tarde`
      : isError
        ? 'Chat indisponível no momento'
        : 'Aguarde a conclusão da análise';

  const readOnlyHint =
    !availability.canAsk && messageCount > 0
      ? `${availability.message} Você pode revisar as perguntas anteriores, mas novas perguntas ficarão disponíveis quando o agente concluir.`
      : availability.message || undefined;

  return (
    <>
      <Box
        ref={rootRef}
        onMouseDown={(event) => event.stopPropagation()}
        sx={{ position: 'relative' }}
      >
        <Box sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {menuOpen && (
            <Paper
              elevation={0}
              sx={{
                position: 'absolute',
                bottom: 'calc(100% + 12px)',
                right: 0,
                width: { xs: 280, sm: 300 },
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${alpha(GREENING_BRAND.green, 0.2)}`,
                boxShadow: `0 12px 40px ${alpha(GREENING_BRAND.green, 0.22)}`,
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${GREENING_BRAND.green} 0%, ${GREENING_BRAND.teal} 100%)`,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                    <GreeningBrandLabel fontWeight={800} fontSize="1.05rem" onDark />
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ opacity: 0.92, display: 'block', mt: 0.5, lineHeight: 1.35 }}
                  >
                    {GREENING_FAB_MENU_SUBTITLE}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={closeMenu}
                  sx={{ color: '#fff', mt: -0.25, mr: -0.5, flexShrink: 0 }}
                  aria-label="Fechar menu"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.grey[50], 0.95) }}>
                {isStudying && (
                  <Chip
                    size="small"
                    label={availability.fabHint || 'Em andamento'}
                    color="warning"
                    variant="outlined"
                    sx={{ mb: 1, width: '100%', justifyContent: 'flex-start' }}
                  />
                )}
                {isReady && (
                  <Chip
                    size="small"
                    icon={<Check sx={{ fontSize: '14px !important' }} />}
                    label="Perguntas liberadas"
                    color="success"
                    variant="outlined"
                    sx={{ mb: 1, width: '100%', justifyContent: 'flex-start' }}
                  />
                )}
                <Box
                  component="button"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenChat();
                  }}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: 1.5,
                    border: 'none',
                    borderRadius: 2,
                    cursor: availability.kind === 'loading' ? 'wait' : 'pointer',
                    textAlign: 'left',
                    color: '#fff',
                    background: `linear-gradient(135deg, ${GREENING_BRAND.green} 0%, ${GREENING_BRAND.teal} 100%)`,
                    boxShadow: `0 4px 14px ${alpha(GREENING_BRAND.green, 0.35)}`,
                    transition: 'box-shadow 0.2s ease, transform 0.15s ease',
                    opacity: availability.kind === 'loading' ? 0.7 : 1,
                    '&:hover': {
                      transform: availability.kind === 'loading' ? 'none' : 'translateY(-1px)',
                      boxShadow: `0 6px 18px ${alpha(GREENING_BRAND.teal, 0.4)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      bgcolor: alpha('#fff', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {isStudying ? (
                      <CircularProgress size={22} sx={{ color: '#fff' }} />
                    ) : (
                      <Forum sx={{ fontSize: 22, color: '#fff' }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} color="inherit" sx={{ lineHeight: 1.25 }}>
                      <GreeningChatTitle variant="body2" onDark />
                    </Typography>
                    {chatMenuDescription && (
                      <Typography variant="caption" sx={{ opacity: 0.92, display: 'block', mt: 0.25, lineHeight: 1.3 }}>
                        {chatMenuDescription}
                      </Typography>
                    )}
                  </Box>
                  {messageCount > 0 && (
                    <Chip
                      label={messageCount}
                      size="small"
                      sx={{
                        flexShrink: 0,
                        height: 24,
                        minWidth: 28,
                        bgcolor: alpha('#fff', 0.22),
                        color: '#fff',
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Paper>
          )}

          <Paper
            component="button"
            type="button"
            elevation={0}
            onClick={(event) => {
              event.stopPropagation();
              setMenuOpen((prev) => !prev);
            }}
            aria-label={menuOpen ? `Recolher menu ${GREENING_AGENT_LABEL}` : `Abrir menu ${GREENING_AGENT_LABEL}`}
            aria-expanded={menuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              pl: 1,
              pr: 1.5,
              py: 0.75,
              borderRadius: 999,
              border: 'none',
              cursor: 'pointer',
              color: 'inherit',
              background: `linear-gradient(135deg, ${alpha('#fff', 0.98)} 0%, ${alpha(GREENING_BRAND.green, isReady ? 0.12 : 0.06)} 100%)`,
              boxShadow: menuOpen
                ? `0 8px 28px ${alpha(GREENING_BRAND.teal, 0.35)}`
                : `0 6px 20px ${alpha(GREENING_BRAND.green, 0.28)}`,
              outline: `2px solid ${alpha(fabOutlineColor, menuOpen ? 0.65 : isReady ? 0.55 : 0.35)}`,
              animation: isReady && !menuOpen ? `${pulseRing} 2.4s ease-out infinite` : undefined,
              transition: 'box-shadow 0.2s ease, outline-color 0.2s ease, transform 0.15s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: `0 10px 28px ${alpha(GREENING_BRAND.teal, 0.38)}`,
                outlineColor: alpha(fabOutlineColor, 0.65),
              },
            }}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              badgeContent={fabBadgeContent}
              color={fabBadgeColor}
              invisible={fabBadgeContent === undefined}
              sx={fabBadgeSx}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#fff',
                  border: `1px solid ${alpha(GREENING_BRAND.green, 0.2)}`,
                  boxShadow: `0 2px 8px ${alpha(GREENING_BRAND.green, 0.15)}`,
                }}
              >
                <GreeningIcon sx={{ fontSize: 32, color: GREENING_BRAND.green }} />
              </Box>
            </Badge>
            <Box sx={{ textAlign: 'left', lineHeight: 1.15, minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.5, display: 'block' }}>
                <GreeningBrandLabel fontWeight={800} fontSize="0.7rem" />
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color={isReady ? 'success.dark' : 'text.primary'}
                sx={{ fontSize: '0.82rem' }}
              >
                {menuOpen ? 'Fechar' : 'Chat'}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>

      <GreeningChatUnavailableDialog
        open={unavailableOpen}
        onClose={() => setUnavailableOpen(false)}
        availability={availability}
      />

      <GreeningChatDialog
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        referenceId={greening.legislationId ? String(greening.legislationId) : null}
        items={items}
        loading={greening.historyLoading}
        onAsk={greening.askQuestion}
        asking={greening.asking}
        disabled={!availability.canAsk}
        disabledHint={readOnlyHint}
        maxHeight={480}
      />
    </>
  );
}

export default GreeningChatFab;
